import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { IMAGERY } from "../lib/imagery";
import { accent } from "../lib/accent";

export default function About() {
  const { t } = useLang();

  return (
    <>
      <section
        data-testid="about-hero"
        className="relative -mt-20 pt-40 pb-24 md:pb-32 bg-obsidian text-white overflow-hidden"
      >
        <img
          src={IMAGERY.skyline}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="container-x relative z-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-10 animate-fade-in-up">
            <p className="eyebrow-light">{t.about.eyebrow}</p>
            <h1 className="display-h1 mt-6 max-w-[20ch] text-white">
              {accent(t.about.title)}
            </h1>
            <div className="section-divider mt-10 mr-auto ml-0" />
            <p className="font-sans text-lg md:text-xl text-white/80 mt-8 max-w-3xl leading-relaxed font-light">
              {t.about.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="container-x py-28 md:py-36 grid grid-cols-12 gap-12">
        <div className="col-span-12 lg:col-span-6 border-l-2 border-champagne pl-10">
          <p className="eyebrow">{t.about.missionLabel}</p>
          <p className="font-serif text-2xl md:text-3xl mt-6 leading-snug max-w-xl">
            {t.about.mission}
          </p>
        </div>
        <div className="col-span-12 lg:col-span-6 border-l-2 border-champagne pl-10">
          <p className="eyebrow">{t.about.visionLabel}</p>
          <p className="font-serif text-2xl md:text-3xl mt-6 leading-snug max-w-xl">
            {t.about.vision}
          </p>
        </div>
      </section>

      {/* Values */}
      <section
        data-testid="values-section"
        className="bg-parchment py-28 md:py-36"
      >
        <div className="container-x grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="eyebrow">{t.about.valuesEyebrow}</p>
            <h2 className="display-h2 mt-5 max-w-[12ch]">
              {accent(t.about.valuesTitle)}
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-obsidian/15">
            {[
              { t: t.about.v1Title, b: t.about.v1Body },
              { t: t.about.v2Title, b: t.about.v2Body },
              { t: t.about.v3Title, b: t.about.v3Body },
            ].map((v, i) => (
              <div
                key={i}
                data-testid={`value-${i + 1}`}
                className="bg-parchment p-8 lg:p-10 hover:bg-ivory transition-colors"
              >
                <p className="font-mono text-[11px] tracking-[0.22em] text-champagne">
                  0{i + 1}
                </p>
                <h3 className="font-serif text-2xl mt-6">{v.t}</h3>
                <p className="mt-5 text-foreground/75 leading-relaxed">{v.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition (4-column glassmorphism cards) */}
      <section
        data-testid="differentials-section"
        className="relative py-32 md:py-40 overflow-hidden"
        style={{ backgroundColor: "#121212" }}
      >
        {/* cinematic architectural backdrop (Gemini Nano Banana render) */}
        <img
          src="/value-proposition-backdrop.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.55]"
        />
        {/* slate-blue + charcoal gradient wash */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(18,18,18,0.55) 0%, rgba(18,18,18,0.40) 35%, rgba(18,18,18,0.85) 75%, rgba(18,18,18,0.97) 100%)",
          }}
        />
        {/* gold/platinum radial glow */}
        <div
          aria-hidden="true"
          className="absolute -top-60 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(212,175,55,0.10) 0%, rgba(148,163,184,0.05) 40%, rgba(212,175,55,0) 70%)",
          }}
        />
        {/* faint geometric grid suggesting architectural structure */}
        <div
          aria-hidden="true"
          className="absolute inset-0 grid-pattern opacity-30 pointer-events-none"
        />

        <div className="container-x relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <p className="eyebrow">{t.about.diffEyebrow}</p>
            <h2 className="display-h2 mt-5 text-white">
              {accent(t.about.diffTitle)}
            </h2>
            <div className="section-divider mt-8" />
            <p className="font-sans text-base md:text-lg text-white/75 mt-8 leading-relaxed font-light">
              {t.about.diffSub}
            </p>
          </div>

          {/* Generous spacing: gap-10 on lg, breathing room around the grid */}
          <div className="mt-24 md:mt-28 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {[
              { n: "01", title: t.about.vp1Title, sub: t.about.vp1Sub, body: t.about.vp1Body },
              { n: "02", title: t.about.vp2Title, sub: t.about.vp2Sub, body: t.about.vp2Body },
              { n: "03", title: t.about.vp3Title, sub: t.about.vp3Sub, body: t.about.vp3Body },
              { n: "04", title: t.about.vp4Title, sub: t.about.vp4Sub, body: t.about.vp4Body },
            ].map((card, i) => (
              <article
                key={i}
                data-testid={`vp-card-${i + 1}`}
                className="relative p-8 lg:p-10 flex flex-col gap-5 min-h-[420px] overflow-hidden transition-all duration-500"
                style={{
                  background: "rgba(20, 24, 36, 0.55)",
                  backdropFilter: "blur(14px) saturate(1.1)",
                  WebkitBackdropFilter: "blur(14px) saturate(1.1)",
                  border: "1px solid rgba(148, 163, 184, 0.12)",
                  borderRadius: "2px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(212,175,55,0.40)";
                  e.currentTarget.style.transform = "translateY(-6px)";
                  e.currentTarget.style.boxShadow =
                    "0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(212,175,55,0.15) inset";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(148,163,184,0.12)";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {/* Large low-opacity numeral, top right */}
                <span
                  aria-hidden="true"
                  className="absolute top-4 right-5 font-serif text-[88px] lg:text-[112px] leading-none select-none pointer-events-none tracking-tight"
                  style={{
                    color: "rgba(212, 175, 55, 0.10)",
                    fontWeight: 600,
                    letterSpacing: "-0.04em",
                  }}
                >
                  {card.n}
                </span>

                {/* Sub-header (gold uppercase) */}
                <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-champagne font-medium relative z-10">
                  {card.sub}
                </p>

                {/* Title (Playfair) */}
                <h3 className="font-serif text-xl lg:text-2xl text-white leading-tight relative z-10 max-w-[16ch]">
                  {card.title}
                </h3>

                {/* Body (Montserrat light) */}
                <p className="font-sans text-[14px] text-white/65 leading-relaxed font-light mt-auto relative z-10">
                  {card.body}
                </p>

                {/* Subtle bottom accent line */}
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 left-0 h-px bg-gradient-to-r from-champagne/60 to-transparent"
                  style={{ width: "40%" }}
                />
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section
        data-testid="leadership-section"
        className="relative bg-obsidian text-white overflow-hidden"
      >
        <div className="container-x relative z-10 py-28 md:py-40">
          {/* Section eyebrow */}
          <div className="text-center max-w-3xl mx-auto">
            <p className="eyebrow">{t.about.leadEyebrow}</p>
            <h2 className="display-h2 mt-5 text-white max-w-[18ch] mx-auto">
              {accent(t.about.lead1Title)}
            </h2>
            <div className="section-divider mt-8" />
            <p className="font-sans text-base md:text-lg text-white/75 mt-8 leading-relaxed font-light">
              {t.about.lead1Body}
            </p>
          </div>

          {/* Architectural visual */}
          <div className="mt-20 relative aspect-[21/9] overflow-hidden">
            <img
              src={IMAGERY.leadershipSkyscraper}
              alt="High contrast architectural perspective of a skyscraper"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "brightness(0.85) contrast(1.1) saturate(0.9)" }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(5,10,20,0.30) 0%, rgba(5,10,20,0.05) 50%, rgba(5,10,20,0.65) 100%)",
              }}
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-champagne/15" />
          </div>

          {/* Two-column blocks */}
          <div className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Standard of Strategic Excellence */}
            <article className="lg:col-span-6">
              <span className="font-sans text-[11px] uppercase tracking-[0.22em] text-champagne font-medium">
                01
              </span>
              <span className="block w-12 h-px bg-champagne mt-3 mb-7" />
              <h3 className="font-serif text-2xl md:text-3xl text-white leading-tight max-w-[20ch]">
                {accent(t.about.lead2Title)}
              </h3>
              <p className="font-sans text-base text-white/75 mt-6 leading-relaxed font-light max-w-xl">
                {t.about.lead2Body}
              </p>
            </article>

            {/* Executive Portfolio Oversight */}
            <article className="lg:col-span-6">
              <span className="font-sans text-[11px] uppercase tracking-[0.22em] text-champagne font-medium">
                02
              </span>
              <span className="block w-12 h-px bg-champagne mt-3 mb-7" />
              <h3 className="font-serif text-2xl md:text-3xl text-white leading-tight max-w-[20ch]">
                {accent(t.about.lead3Title)}
              </h3>
              <p className="font-sans text-base text-white/75 mt-6 leading-relaxed font-light max-w-xl">
                {t.about.lead3Body}
              </p>

              {/* Pillars */}
              <div className="mt-10 space-y-7 border-l border-champagne/30 pl-7">
                {[
                  { title: t.about.pillar1Title, body: t.about.pillar1Body },
                  { title: t.about.pillar2Title, body: t.about.pillar2Body },
                ].map((p, i) => (
                  <div key={i}>
                    <p className="font-serif text-lg text-white">{p.title}</p>
                    <p className="font-sans text-[14px] text-white/65 mt-2 leading-relaxed font-light max-w-md">
                      {p.body}
                    </p>
                  </div>
                ))}
              </div>
            </article>
          </div>

          {/* Statement of intent: centered cream blockquote */}
          <div className="mt-24 md:mt-32 max-w-4xl mx-auto">
            <div
              className="relative px-8 md:px-14 py-16 md:py-20 text-center"
              style={{
                background: "rgba(247, 244, 236, 0.04)",
                border: "1px solid rgba(212, 175, 55, 0.30)",
              }}
            >
              <p className="font-serif italic text-2xl md:text-3xl text-white leading-[1.4] tracking-tight">
                <span aria-hidden="true" className="text-champagne mr-2">“</span>
                {t.about.closeQuote}
                <span aria-hidden="true" className="text-champagne ml-2">”</span>
              </p>
              <span className="block w-12 h-px bg-champagne mx-auto mt-10" />
              <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-champagne mt-5 font-medium">
                Nivne Consulting Group
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-x py-24 md:py-32 grid grid-cols-12 gap-10 items-end">
        <div className="col-span-12 lg:col-span-8">
          <h2 className="display-h2 max-w-[20ch]">{accent(t.about.ctaTitle)}</h2>
        </div>
        <div className="col-span-12 lg:col-span-4">
          <Link to="/contact" data-testid="about-cta" className="group obsidian-btn">
            {t.about.ctaBtn}
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}
